<template>
    <page-container class="grid grid-cols-12 gap-8">
        <!-- Tabs -->
        <settings-sidebar></settings-sidebar>

        <!-- Tab cards -->
        <page-loading v-if="!team" class="col-span-12 md:col-span-8 lg:col-span-10"></page-loading>
        <div v-else ref="content" class="col-span-12 md:col-span-8 lg:col-span-10">
            <!-- Owner Information -->
            <div v-if="(onCorporate() && hash === 'owner') || !hash" role="tabcard">
                <team-profile
                    :user="user"
                    v-model:team="team"
                    :isTeamAdmin="isTeamAdmin"
                    :impersonator="isImpersonatorOrPlatformAdmin"
                >
                </team-profile>
            </div>

            <template v-if="onCorporate() && teamAndIsParent">
                <div v-if="hash === 'sso-settings' && team && FEATURES.single_sign_on" role="tabcard">
                    <sso-settings :team="team"></sso-settings>
                </div>
            </template>

            <!-- Membership -->
            <div v-if="onCorporate() && hash === 'membership'" role="tabcard">
                <team-users v-if="team && user && !user.isUsingSingleSignOn" :teamId="props.teamId" />
            </div>

            <div v-if="onCorporate() && hash === 'phone-numbers'" role="tabcard">
                <team-phone-numbers />
            </div>

            <div v-if="onCorporate() && hash === 'phone-number-pools'" role="tabcard">
                <team-phone-number-pools />
            </div>

            <div v-if="onCorporate() && hash === 'customattributes'" role="tabcard">
                <div v-if="team">
                    <team-custom-attributes :user="user" :team="team" />
                </div>
            </div>

            <div v-if="hash === 'shift-to-text' && FEATURES.shift_to_text" role="tabcard">
                <div v-if="team">
                    <shift-to-text />
                </div>
            </div>

            <div v-if="onCorporate() && hash === 'contacts-exports'" role="tabcard">
                <div>
                    <contact-export-settings />
                </div>
            </div>

            <template v-if="currentGroup">
                <div v-if="hash === 'group-details'" role="tabcard">
                    <group-form :groupId="currentGroup.group_id" />
                </div>

                <div v-if="hash === 'group-users' && ['admin'].includes(roleOnCurrentGroup)" role="tabcard">
                    <group-user-list
                        :groupId="currentGroup.group_id"
                        :groupName="currentGroup.name"
                        :teamId="props.teamId"
                    >
                        <template v-slot:page-header> Users </template>
                    </group-user-list>
                </div>
            </template>

            <template v-if="currentOrgUnit">
                <div v-if="hash === 'current-org-unit-details'" role="tabcard">
                    <org-unit-form :teamId="props.teamId" :orgUnitId="currentOrgUnit?.id"></org-unit-form>
                </div>

                <div v-if="hash === 'current-org-unit-users' && roleOnCurrentOrgUnit === 'admin'" role="tabcard">
                    <org-unit-user-list
                        :teamId="props.teamId"
                        :orgUnitId="currentOrgUnit?.id"
                        :orgUnitName="currentOrgUnit.name"
                    >
                        <template v-slot:page-header>Users</template>
                    </org-unit-user-list>
                </div>

                <div v-if="hash === 'current-org-unit-groups' && roleOnCurrentOrgUnit === 'admin'" role="tabcard">
                    <org-unit-groups
                        :teamId="props.teamId"
                        :orgUnitId="currentOrgUnit?.id"
                        :orgUnitName="currentOrgUnit.name"
                    >
                        <template v-slot:page-header>Groups</template>
                    </org-unit-groups>
                </div>

                <div
                    v-if="hash.startsWith('current-org-unit-groups/') && roleOnCurrentOrgUnit === 'admin'"
                    role="tabcard"
                >
                    <group-form :groupId="hash.split('/')[1]"> </group-form>
                </div>

                <div v-if="hash === 'current-org-unit-collections'" role="tabcard">
                    <group-collections-list :orgUnitId="currentOrgUnit.id" />
                </div>
            </template>

            <div v-if="onCorporate() && isOrgUnitGroupsTab && orgUnitId" role="tabcard">
                <org-unit-groups :orgUnitId="orgUnitId" />
            </div>

            <div v-if="onCorporate() && hash === 'org-units'" role="tabcard">
                <org-units-list />
            </div>

            <div v-if="onCorporate() && hash === 'groups'" role="tabcard">
                <groups-list />
            </div>

            <div v-if="onCorporate() && hash === 'group-collections'" role="tabcard">
                <group-collections-list />
            </div>

            <div v-if="onCorporate() && hash === 'message-standards'" role="tabcard">
                <message-standards-list />
            </div>

            <div v-if="onCorporate() && hash === 'analytics-goals' && FEATURES.contact_groups" role="tabcard">
                <analytics-goals />
            </div>

            <div v-if="onCorporate() && hash === 'cost-visibility' && FEATURES.contact_groups" role="tabcard">
                <cost-visibility />
            </div>

            <!-- Billing Tab Panes -->
            <template v-if="onCorporate() && (ownsTeam || isTeamAdmin)">
                <template v-if="FEATURES.new_billing_implementation">
                    <!-- Proforma Invoices -->
                    <div v-if="hash === 'proforma-invoices' && team && team.status !== 'closed'" role="tabcard">
                        <team-proforma-invoices :team-id="team.id" :payment-terms-days="team.payment_terms_days" />
                    </div>

                    <!-- Historical Invoices -->
                    <div v-if="hash === 'historical-invoices' && team" role="tabcard">
                        <team-historical-invoices :team-id="team.id" />
                    </div>

                    <!-- Billing Details -->
                    <div v-if="hash === 'billing-details'" role="tabcard">
                        <team-billing-details :team="team" :isAdminOrOwner="isTeamAdmin" />
                    </div>
                </template>
                <template v-else>
                    <!-- Subscription -->
                    <div v-if="hash === 'subscription'" role="tabcard">
                        <div v-if="user && team">
                            <spark-subscription
                                :user="user"
                                :team="team"
                                :is-impersonator-or-platform-admin="isImpersonatorOrPlatformAdmin"
                                :cashier-path="cashierPath"
                                :team-subscription="teamSubscription"
                                :team-last-payment-id="teamLastPaymentId"
                                :team-message-balance="teamMessageBalance"
                                :countries-list="countriesList"
                                :has-impersonator="hasImpersonator"
                            ></spark-subscription>
                        </div>
                    </div>

                    <!-- Payment Information -->
                    <div v-if="hash === 'payment-information'" role="tabcard">
                        <div v-if="user && team">
                            <payment-information :team="team" :collects-billing-address="collectsBillingAddress">
                            </payment-information>
                        </div>
                    </div>

                    <!-- Invoices -->
                    <div v-if="hash === 'invoices'" role="tabcard">
                        <div v-if="team">
                            <invoice-settings :team="team" :owns-team="ownsTeam" :isTeamAdmin="isTeamAdmin">
                            </invoice-settings>
                        </div>
                    </div>
                    <div v-if="hash === 'plan-information'" role="tabcard">
                        <plan-information
                            v-model="team"
                            :teamPlan="props.teamPlan"
                            :teamMessageBalance="props.teamMessageBalance"
                            :teamSumBalanceAmount="props.teamSumBalanceAmount"
                            :isAdminOrOwner="isTeamAdmin"
                        ></plan-information>
                    </div>
                </template>
            </template>
        </div>
    </page-container>
</template>

<script setup>
import { ref, inject, computed, watch, nextTick } from 'vue';
import PageContainer from '~/components/general/PageContainer.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import SettingsSidebar from './SettingsSidebar.vue';
import TeamProfile from '../teams/profile/TeamProfile.vue';
import SsoSettings from '../teams/sso/SsoSettings.vue';
import TeamUsers from '../teams/membership/TeamUsers.vue';
import TeamCustomAttributes from '../teams/TeamCustomAttributes.vue';
import TeamPhoneNumbers from '../teams/phone-numbers/TeamPhoneNumbers.vue';
import ShiftToText from '../teams/shift-to-text/ShiftToText.vue';
import SparkSubscription from '../subscription/SparkSubscription.vue';
import PaymentInformation from '../payment-information/PaymentInformation.vue';
import InvoiceSettings from '../invoices/InvoiceSettings.vue';
import TeamProformaInvoices from '../billing/TeamProformaInvoices.vue';
import TeamHistoricalInvoices from '../billing/TeamHistoricalInvoices.vue';
import TeamBillingDetails from '../billing/TeamBillingDetails.vue';
import ContactExportSettings from '../teams/contact-export-settings/ContactExportSettings.vue';
import GroupForm from '../teams/groups/form/GroupForm.vue';
import GroupUserList from '../teams/groups/form/users/GroupUserList.vue';
import GroupsList from '../teams/groups/list/GroupsList.vue';
import GroupCollectionsList from '../teams/group-collections/list/GroupCollectionsList.vue';
import MessageStandardsList from '../teams/message-standards/list/MessageStandardsList.vue';
import PlanInformation from '../teams/plan-information/PlanInformation.vue';
import { useBrowserLocation } from '@vueuse/core';
import axios from '~/services/axios';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import TeamPhoneNumberPools from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPools.vue';
import AnalyticsGoals from '../teams/analytics-goals/AnalyticsGoals.vue';
import CostVisibility from '../teams/cost-visibility/CostVisibility.vue';
import OrgUnitsList from '~/components/settings/teams/org-units/list/OrgUnitsList.vue';
import OrgUnitGroups from '../teams/org-units/groups/OrgUnitGroups.vue';
import OrgUnitForm from '~/components/settings/teams/org-units/form/OrgUnitForm.vue';
import OrgUnitUserList from '~/components/settings/teams/org-units/form/users/OrgUnitUserList.vue';

const FEATURES = inject('FEATURES');

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    user: Object,
    ownsTeam: Boolean,
    onTeam: Boolean,
    teamAndIsParent: Boolean,
    isImpersonatorOrPlatformAdmin: Boolean,
    isTeamAdmin: Boolean,
    defaultRole: String,
    countriesList: Array,
    cashierPath: String,
    teamPlan: Object,
    teamSubscription: Boolean,
    teamLastPaymentId: Number,
    teamMessageBalance: Number,
    teamSumBalanceAmount: Number,
    hasImpersonator: Boolean,
});

const { onCorporate, currentGroup, roleOnCurrentGroup, currentOrgUnit, roleOnCurrentOrgUnit } = useCurrentGroup();

const collectsBillingAddress = Spark.collectsBillingAddress;

const team = ref(null);

const getTeam = () => {
    axios.get(`/settings/teams/json/${props.teamId}`).then((response) => {
        team.value = response.data;
    });
};

getTeam();

const location = useBrowserLocation();

const hash = computed(() => location.value.hash?.replace('#/', '').replace('#', ''));

if (!hash.value) {
    location.value.hash = '#/owner';
}

const isOrgUnitGroupsTab = computed(() => {
    return /^org-units(\/\d+(\/groups)?)?$/.test(hash.value);
});

const orgUnitId = computed(() => {
    const match = hash.value.match(/^org-units\/(\d+)/);
    return match ? Number(match[1]) : null;
});

const content = ref();
watch(
    hash,
    async () => {
        await nextTick();
        if (!content.value || window.innerWidth >= 768) {
            return;
        }
        content.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    { immediate: true }
);

Bus.$on('updateTeam', () => {
    getTeam();
});
</script>
